import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Link, Alert, CircularProgress } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth.service';
import { AuthSplitLayout, authFooterSx } from './AuthSplitLayout';
import { AuthTextField } from './AuthTextField';

type VerifyStatus = 'verifying' | 'verified' | 'failed';

/** Landing page for the link in the verification email; offers a resend when the link is bad or expired. */
export const VerifyEmailForm = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState<VerifyStatus>(token ? 'verifying' : 'failed');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  // A token works once, so StrictMode's double effect run must not submit it twice
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!token || submittedRef.current) return;
    submittedRef.current = true;
    authService.verifyEmail(token)
      .then(() => setStatus('verified'))
      .catch(() => setStatus('failed'));
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResending(true);
    try {
      await authService.resendVerification(email);
      setResendMessage(t('verify.resent'));
    } catch {
      setResendMessage(t('verify.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthSplitLayout title={t('verify.title')} subtitle={t(`verify.subtitle.${status}`)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {status === 'verifying' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
        )}
        {status === 'verified' && (
          <>
            <Alert severity="success">{t('verify.success')}</Alert>
            <Button fullWidth variant="contained" component={RouterLink} to="/login" sx={{ height: 42 }}>
              {t('verify.signIn')}
            </Button>
          </>
        )}
        {status === 'failed' && (
          <Box component="form" onSubmit={handleResend} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Alert severity="error">{t('verify.failed')}</Alert>
            <AuthTextField
              type="email"
              label={t('fields.email')}
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={resending}
            />
            {resendMessage && <Alert severity="info">{resendMessage}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={resending || !email.trim()} sx={{ height: 42 }}>
              {resending ? t('verify.resending') : t('verify.resend')}
            </Button>
          </Box>
        )}
        <Box sx={authFooterSx}>
          <Link component={RouterLink} to="/login" underline="hover">{t('forgot.backToSignIn')}</Link>
        </Box>
      </Box>
    </AuthSplitLayout>
  );
};
